import { LocationSettingsContext } from '../context/context';
import { getStoredLocation, storeLocation } from '../utils/locationStorage';

// Newcastle, Australia coordinates as final fallback
const NEWCASTLE_FALLBACK: LocationSettingsContext = {
    latitude: -32.926034114093284,
    longitude: 151.76150787192498,
    zoom: 13
};

// World view coordinates as intermediate fallback
const WORLD_FALLBACK: LocationSettingsContext = {
    latitude: 0,
    longitude: 0,
    zoom: 2
};

interface GeolocationResponse {
    latitude: number;
    longitude: number;
    accuracy?: number;
}

interface IPLocationResponse {
    latitude: number;
    longitude: number;
    city?: string;
    country?: string;
}

/**
 * Get user location using browser geolocation API
 */
const getBrowserLocation = (): Promise<GeolocationResponse> => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error('Geolocation not supported'));
            return;
        }

        const options = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
        };

        navigator.geolocation.getCurrentPosition(
            (position) => {
                // console.log('Browser geolocation result:', {
                //     latitude: position.coords.latitude,
                //     longitude: position.coords.longitude,
                //     accuracy: position.coords.accuracy,
                //     timestamp: position.timestamp
                // });
                resolve({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                });
            },
            (error) => {
                console.warn('Browser geolocation failed:', error.message);
                reject(error);
            },
            options
        );
    });
};

/**
 * Get approximate location using IP address
 */
const getIPLocation = async (): Promise<IPLocationResponse> => {
    try {
        const response = await fetch('https://ipapi.co/json/');
        
        if (!response.ok) {
            throw new Error(`IP location service responded with ${response.status}`);
        }

        const data = await response.json();
        
        if (data.latitude && data.longitude) {
            return {
                latitude: parseFloat(data.latitude),
                longitude: parseFloat(data.longitude),
                city: data.city,
                country: data.country_name
            };
        } else {
            throw new Error('Invalid location data received');
        }
    } catch (error) {
        console.warn('IP location service failed:', error);
        throw error;
    }
};

/**
 * Convert coordinates to LocationSettingsContext with appropriate zoom
 */
const coordinatesToLocationSettings = (
    latitude: number, 
    longitude: number, 
    source: 'browser' | 'ip' | 'fallback'
): LocationSettingsContext => {
    let zoom: number;
    
    switch (source) {
        case 'browser':
            zoom = 12;
            break;
        case 'ip':
            zoom = 8;
            break;
        case 'fallback':
        default:
            zoom = 13;
            break;
    }

    return {
        latitude,
        longitude,
        zoom
    };
};

/**
 * Main function to get user location using hybrid approach
 * Always prefer fresh browser geolocation if permission is granted.
 */
export const getUserLocation = async (): Promise<LocationSettingsContext> => {
    // Check geolocation permission
    let permission: PermissionState | 'not-supported' = 'not-supported';
    if ('permissions' in navigator) {
        try {
            const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
            permission = result.state;
        } catch {}
    }

    // If permission is granted, always try browser geolocation first
    if (permission === 'granted') {
        try {
            const browserLocation = await getBrowserLocation();
            const location = coordinatesToLocationSettings(
                browserLocation.latitude,
                browserLocation.longitude,
                'browser'
            );
            storeLocation(location, 'browser');
            return location;
        } catch {}
    }

    // Otherwise, use stored location if available
    const stored = getStoredLocation();
    if (stored) {
        console.log('Using stored location:', stored);
        return stored.location;
    }

    // Try browser geolocation (if not already tried)
    try {
        const browserLocation = await getBrowserLocation();
        const location = coordinatesToLocationSettings(
            browserLocation.latitude,
            browserLocation.longitude,
            'browser'
        );
        storeLocation(location, 'browser');
        return location;
    } catch {}

    // Try IP-based location
    try {
        const ipLocation = await getIPLocation();
        const location = coordinatesToLocationSettings(
            ipLocation.latitude,
            ipLocation.longitude,
            'ip'
        );
        storeLocation(location, 'ip');
        return location;
    } catch {}

    // Fallback to Newcastle
    storeLocation(NEWCASTLE_FALLBACK, 'fallback');
    return NEWCASTLE_FALLBACK;
};

/**
 * Check if geolocation is supported
 */
export const isGeolocationSupported = (): boolean => {
    return 'geolocation' in navigator;
};

/**
 * Check geolocation permission status
 */
export const checkGeolocationPermission = async (): Promise<PermissionState | 'not-supported'> => {
    if (!isGeolocationSupported()) {
        return 'not-supported';
    }

    try {
        if ('permissions' in navigator) {
            const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
            return permission.state;
        }
        return 'prompt';
    } catch (error) {
        console.warn('Could not check geolocation permission:', error);
        return 'prompt';
    }
};

/**
 * Request geolocation permission explicitly
 */
export const requestGeolocationPermission = async (): Promise<boolean> => {
    if (!isGeolocationSupported()) {
        return false;
    }

    try {
        // Try to get a quick position to trigger permission request
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        
        return true;
    } catch (error) {
        console.warn('Geolocation permission denied or failed:', error);
        return false;
    }
};

export { NEWCASTLE_FALLBACK, WORLD_FALLBACK }; 